import { useEffect, useState } from 'react';
import { useT } from '@hana/i18n';
import './screens.i18n';
import { IconArrowLeft, IconBell, IconCalendarRepeat, IconChecklist, IconDots, IconEye, IconFileCheck, IconFilePlus, IconFileText, IconPlus } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { CONTRACT_DOCK_NOTE, CONTRACT_DOCK_TITLE, CONTRACT_MORE, CONTRACT_RULES, CONTRACT_TABS, CONTRACT_TODO, contractKpis, contractLife, contractSoon, type ContractTab, type TodoTone } from './contract';
import { ContractIssued, ContractTemplates } from './ContractTabs';
import { ContractIssue } from './ContractIssue';
import s from './hr.module.css';
import d from './decision.module.css';

// Mint-LaborContract · tab Tổng quan (design-specs/Mint-LaborContract.md). Chỉ đọc; nút ghi báo toast. Không lương / CCCD.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const TONE: Record<TodoTone, string> = { warn: s.tWarn ?? '', err: s.tErr ?? '', pri: s.tPri ?? '', mute: s.tMute ?? '' };
const ACT_ICON: Record<string, typeof IconEye> = { 'Gia hạn': IconCalendarRepeat, 'Lập HĐ KXĐ': IconFilePlus, 'Duyệt mẫu': IconFileCheck, Xem: IconEye };

export function HrContract() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { data, error, isLoading, isError, refetch } = useList('hrContract', { limit: 100 });
  const { data: tplData } = useList('hrContractTemplate', { limit: 100 });
  const forced = readState();
  const [tab, setTab] = useState<ContractTab>('ov');
  const [dense, setDense] = useState<'std' | 'compact'>('std');
  const [more, setMore] = useState(false);
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const contracts = data?.items ?? [];

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (contracts.length === 0) state = 'empty';

  const noWrite = () => toast({ msg: t('hr.s.noWrite') });
  const pickTab = (k: ContractTab) => (k === 'ov' || k === 'iss' || k === 'tpl' || k === 'issued' ? setTab(k) : toast({ msg: 'Tab này chưa mở ở bản này' }));
  const tabs = CONTRACT_TABS.map((t) => ({ key: t.key, label: t.label, n: 'n' in t ? t.n : undefined }));
  const kpis = contractKpis(contracts, tplData?.items ?? []);
  const life = contractLife(contracts);

  return (
    <Page
      kind="table"
      dock={wide ? (
        <div className={d.dock}>
          <h2>{CONTRACT_DOCK_TITLE}</h2>
          <b className={d.h3}>{t('hr.s.expSoon')}</b>
          {contractSoon(contracts).map((x) => (
            <div key={x.sub} className={d.soonCard}>
              <div><b>{x.k}</b><span>{x.left}</span></div>
              <span>{x.sub}</span>
              <button type="button" className={s.evAct} onClick={() => pickTab('iss')}>
                <IconCalendarRepeat size={16} stroke={1.8} aria-hidden="true" />{t('hr.s.renew')}
              </button>
            </div>
          ))}
          <b className={d.h3}>{t('hr.s.tplPending')}</b>
          <div className={d.draftNote}>{tplData?.items.length ?? 0}/{tplData?.items.length ?? 0} mẫu còn Draft — bản phát hành từ các mẫu này là NHÁP</div>
          <div className={d.note}>{CONTRACT_DOCK_NOTE}</div>
        </div>
      ) : undefined}
    >
      <PageHeader
        title="Hợp đồng lao động"
        lead={<HeaderTool label="Về Nhân sự" icon={IconArrowLeft} onClick={() => void navigate({ to: '/ezy/hr' })} />}
        actions={
          <>
            <Segmented kind="toggle" label="Mật độ" value={dense} onChange={setDense} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />
            <HeaderTool label="Thông báo · 4 mới" icon={IconBell} onClick={() => toast({ msg: '4 thông báo mới · mở ở Việc & Duyệt' })} />
            {!wide && <HeaderTool label="Hạn & mẫu chờ duyệt" icon={IconChecklist} onClick={() => toast({ msg: 'Hạn & mẫu chờ duyệt · chưa mở ở bản này' })} />}
            <HeaderTool label="Thêm thao tác" icon={IconDots} active={more} onClick={() => setMore((v) => !v)} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => pickTab('iss')}>{t('hr.s.issueContract')}</Button>
          </>
        }
      />
      {more && (
        <div role="menu" aria-label="Thêm thao tác" className={s.card}>
          {CONTRACT_MORE.map(([k, sub]) => (
            <button key={k} type="button" role="menuitem" className={s.evAct} onClick={() => { setMore(false); toast({ msg: `${k} · chưa mở ghi ở bản này` }); }} title={sub}>{k}</button>
          ))}
        </div>
      )}
      <div className={s.page}>
        <div className={s.tabs}>
          <Segmented label="Hợp đồng lao động" value={tab} onChange={pickTab} items={tabs} />
        </div>
        <StateView
          state={state}
          title={state === 'empty' ? 'Chưa có mẫu hợp đồng' : undefined}
          desc={state === 'empty' ? 'Nạp 5 mẫu HĐ lao động chuẩn để bắt đầu.' : undefined}
          icon={state === 'empty' ? <IconFileText size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('hr.s.retry')}</Button> : state === 'empty' ? <Button variant="primary" onClick={noWrite}>{t('hr.s.load5')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          <div className={s.kpis}>
            {kpis.map((k) => (
              <div key={k.k} className={s.kpi}>
                <div className={s.kpiK}>{k.k}</div>
                <div className={s.kpiV}>
                  <b className={k.tone === 'warn' ? s.warn : undefined}>{k.v}</b>
                  <span>{k.u}</span>
                </div>
              </div>
            ))}
          </div>
          {tab === 'iss' && <ContractIssue />}
          {tab === 'tpl' && <ContractTemplates />}
          {tab === 'issued' && <ContractIssued contracts={contracts} onRenew={() => pickTab('iss')} onTemplates={() => pickTab('tpl')} />}
          {tab === 'ov' && <>
          <div className={s.cGrid}>
            <section className={s.card} aria-label="Cần xử lý">
              <div className={s.cardHd}>
                <h2>{t('hr.s.needAction')}</h2>
                <span>{CONTRACT_TODO.length} việc</span>
              </div>
              {CONTRACT_TODO.map((t) => {
                const Ico = ACT_ICON[t.act];
                return (
                  <div key={t.k} className={s.cTodo}>
                    <span className={`${s.evKind} ${TONE[t.tone]}`}>{t.tag}</span>
                    <div className={s.evTx}>
                      <b>{t.k}</b>
                      <div>{t.sub}</div>
                    </div>
                    <button type="button" className={s.evAct} onClick={() => (t.to === 'ov' ? undefined : pickTab(t.to || 'ov'))}>
                      {Ico && <Ico size={16} stroke={1.8} aria-hidden="true" />}
                      {t.act}
                    </button>
                  </div>
                );
              })}
            </section>
            <div className={s.cCol}>
              <section className={s.card} aria-label="Vòng đời hợp đồng">
                <div className={s.cardHd}><h2>{t('hr.s.contractLife')}</h2></div>
                {life.map((l, i) => (
                  <div key={l.k} className={s.cLife}>
                    <i>{i + 1}</i>
                    <div>
                      <b>{l.k}</b>
                      <div>{l.sub}</div>
                    </div>
                    <span>{l.c}</span>
                  </div>
                ))}
              </section>
              <section className={s.card} aria-label="Kiểm pháp lý">
                <div className={s.cardHd}><h2>{t('hr.s.legalCheck')}</h2></div>
                {CONTRACT_RULES.map(([k, sub]) => (
                  <div key={k} className={s.cRule}>
                    <b>{k}</b>
                    <div>{sub}</div>
                  </div>
                ))}
              </section>
            </div>
          </div>
        </>}
        </StateView>
      </div>
    </Page>
  );
}
